import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import Loader from "react-loaders";
import Paper from "@mui/material/Paper";

import MapActioner from "services/map/actioner";

import ParkkiTheme from "themes/ParkkiTheme";

import DaysHoursSlider from "components/DaysHoursSlider";
import FloorsButton from "components/FloorsButton";
import MapCheckboxContainer from "components/MapCheckboxContainer";
import MapInfo from "components/MapInfo";
import RecenterButton from "components/RecenterButton";
import SelectField from "components/SelectField";
import Checkbox from "./Checkbox";
// @ts-expect-error - file is not written in TS.
import ParkStatisticsActioner from "services/parkStatistics/actioner";

const Container = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  height: 100%;
  background-color: rgba(236, 235, 235, 0.9);
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

const SelectAreaContainer = styled(Paper)`
  min-width: 200px;
  .MuiInputBase-root {
    margin-top: 0px !important;
  }
`;

const SliderContainer = styled.div`
  position: absolute;
  bottom: 0px;
  z-index: 400;
  padding-right: 60px;
  padding-left: 60px;
  margin-bottom: 40px;
  width: 70%;
  max-width: 600px;
`;

const ContentContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
`;

const StyledLoader = styled(Loader)`
  margin-top: 40px;
  color: ${ParkkiTheme.colors.blue};
`;

const LoaderContainer = styled.div`
  display: flex;
  flex: 1;
  background-color: rgba(236, 235, 235, 0.5);
  height: 100%;
  justify-content: center;
`;

const RightHeader = styled.div`
  position: absolute;
  top: 57px;
  right: 0px;
  z-index: 500;
  padding: 10px;
`;

const RightHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  align-items: flex-end;
`;

const ButtonContainer = styled.div`
  margin-top: 10px;
`;

type Props = {
  withCheckboxOptions?: {
    aisles?: boolean;
    labels?: boolean;
    spaceLabels?: boolean;
  };
  daysHoursSlider: boolean;
  contract: {};
  floorsButton: boolean;
  getSliderMaxDaysAllowed: Function;
  mapConfig: {
    currentFloor: number;
    floors: { name: string; level: number }[];
    level: number;
    aislesDisplayed: boolean;
    displaysDisplayed: boolean;
    labelsDisplayed: boolean;
    spaceLabelsDisplayed: boolean;
    aisles: { configs: [] };
  };
  mapComponent: JSX.Element;
  mapInitialized: boolean;
  onRecenter: React.MouseEventHandler<HTMLDivElement>;
  onReloadData: React.MouseEventHandler<HTMLDivElement>;
  onTimestampChange: Function;
  recenterButton: boolean;
  selectedTimestamp: number;
  setConfigParam: Function;
  selectLayersConfig: {
    onAction: Function;
    isSelected: Function;
    layers: { value: any; text: string; area_id?: string }[];
  };
  retrieveAllParkStats: Function;
  onAllStatsChange: Function;
  getAllStats: Function;
};

const AllStatsCheckboxContainer = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  background-color: white;
  padding: 5px;
  border-radius: 4px;
`;

function Map({
  withCheckboxOptions,
  daysHoursSlider,
  contract,
  floorsButton,
  getSliderMaxDaysAllowed,
  mapConfig,
  mapComponent,
  mapInitialized,
  onRecenter,
  onReloadData,
  onTimestampChange,
  recenterButton,
  selectedTimestamp,
  setConfigParam,
  selectLayersConfig,
  retrieveAllParkStats,
  onAllStatsChange,
  getAllStats,
}: Props): JSX.Element {
  const { formatMessage } = useIntl();

  const [allStatsChecked, setAllStatsChecked] = useState(false);
  const [isMapDisabled, setIsMapDisabled] = useState(false);

  const handleAllStatsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsMapDisabled(isChecked);
    setAllStatsChecked(isChecked);
    setConfigParam("mapDisabled", isChecked);
    onAllStatsChange(e.target.checked);
    if (isChecked) {
      retrieveAllParkStats(true); // New parameter to fetch all areas
    } else {
      retrieveAllParkStats();
    }
  };

  const formattedFloors = useMemo(() => {
    if (!floorsButton || !Array.isArray(mapConfig?.floors)) {
      return [];
    }

    return mapConfig?.floors?.map((floor) => ({
      label: formatMessage({ id: `components.floorsButton.${floor.name}` }),
      value: floor?.level,
    }));
  }, [mapConfig?.floors, floorsButton, formatMessage]);

  const sliderMaxDaysAllowed = useMemo(() => {
    if (!daysHoursSlider || !getSliderMaxDaysAllowed) return 0;

    return getSliderMaxDaysAllowed(contract);
  }, [contract, daysHoursSlider, getSliderMaxDaysAllowed]);

  const checkboxOptions = useMemo(() => {
    const displayOptions = {
      aisles: true,
      labels: true,
      displays: true,
      spaceLabels: true,
      ...(withCheckboxOptions || {}),
    };

    return {
      labels: {
        display: displayOptions?.labels,
        onChange: (value: boolean) => {
          if (!value) {
            setConfigParam("aislesDisplayed", false);
          }
          setConfigParam("labelsDisplayed", value);
        },
        value: mapConfig?.labelsDisplayed,
      },
      aisles: {
        display:
          displayOptions?.aisles && mapConfig?.aisles?.configs?.length > 0,
        onChange: (value: boolean) => {
          if (value) {
            setConfigParam("labelsDisplayed", true);
          }
          setConfigParam("aislesDisplayed", value);
        },
        value: mapConfig?.aislesDisplayed,
      },
      displays: {
        display: displayOptions?.displays,
        onChange: (value: boolean) => {
          if (value) {
            setConfigParam("displaysDisplayed", true);
          }
          setConfigParam("displaysDisplayed", value);
        },
        value: mapConfig?.displaysDisplayed,
      },
      spaceLabels: {
        display: displayOptions?.spaceLabels,
        onChange: (value: boolean) => {
          setConfigParam("spaceLabelsDisplayed", value);
        },
        value: mapConfig?.spaceLabelsDisplayed,
      },
    };
  }, [mapConfig, setConfigParam, withCheckboxOptions]);

  const selectedLayers = useMemo(() => {
    return selectLayersConfig?.layers.filter((item) =>
      selectLayersConfig?.isSelected(item)
    );
  }, [selectLayersConfig]);

  function handleRecenter() {
    if (onRecenter) {
      // @ts-ignore
      return onRecenter(window?.currentMapReference?.leafletElement);
    }
  }

  if (!mapInitialized) {
    return (
      <LoaderContainer>
        <StyledLoader active type="ball-beat" />
      </LoaderContainer>
    );
  }

  return (
    <Container>
      {formattedFloors.length > 0 &&
        onAllStatsChange && ( // hide this checkbox when onAllStatsChange is not defined (not used on the current page -- only used on the ParkStatistics page for now)
          <AllStatsCheckboxContainer>
            <Checkbox
              label={formatMessage({ id: "parkStatisticsScene.map.allStats" })}
              checked={isMapDisabled}
              onChange={handleAllStatsChange}
              labelColor={ParkkiTheme.colors.turquoise}
            />
          </AllStatsCheckboxContainer>
        )}
      <RightHeader>
        <RightHeaderContainer>
          {onReloadData && (
            <MapInfo
              onClick={onReloadData}
              label={formatMessage({
                id: "parkStatisticsScene.map.reloadStatsOnZone",
              })}
            />
          )}
          {selectLayersConfig && (
            <SelectAreaContainer>
              <SelectField
                // @ts-ignore
                variant="outlined"
                checkbox
                id="select-area-select"
                label={
                  selectedLayers?.length === 0
                    ? formatMessage({ id: "components.map.selectLayers" })
                    : ""
                }
                values={selectLayersConfig?.layers}
                checked={selectedLayers.map((item) => item.value)}
                onChange={(event: any) => {
                  const newlySelectedValues = event?.target?.value;
                  const newlySelectedLayers = newlySelectedValues.map(
                    (areaId: string) => {
                      return selectLayersConfig?.layers.find(
                        (layer) => layer.area_id === areaId
                      );
                    }
                  );
                  selectLayersConfig?.onAction(newlySelectedLayers);
                }}
              />
            </SelectAreaContainer>
          )}
          {recenterButton && !isMapDisabled && (
            <ButtonContainer>
              <RecenterButton
                recenter={handleRecenter}
                disabled={isMapDisabled}
              />
            </ButtonContainer>
          )}
          {floorsButton && !isMapDisabled && (
            <ButtonContainer>
              <FloorsButton
                setFloor={(value: boolean) =>
                  setConfigParam("currentFloor", value)
                }
                floors={formattedFloors}
                currentFloor={mapConfig?.currentFloor}
                disabled={isMapDisabled}
              />
            </ButtonContainer>
          )}

          {!isMapDisabled && (
            <MapCheckboxContainer
              options={checkboxOptions}
              disabled={isMapDisabled}
            />
          )}
        </RightHeaderContainer>
      </RightHeader>
      <ContentContainer
        style={{ pointerEvents: allStatsChecked ? "none" : "auto" }}
      >
        {mapComponent}
      </ContentContainer>

      {daysHoursSlider && (
        <Footer>
          <SliderContainer>
            <DaysHoursSlider
              selectedTimestamp={selectedTimestamp}
              onTimestampChange={onTimestampChange}
              maxDaysAllowed={sliderMaxDaysAllowed}
            />
          </SliderContainer>
        </Footer>
      )}
    </Container>
  );
}

Map.defaultProps = {
  recenterButton: true,
  floorsButton: false,
  aislesButton: false,
  onReloadData: null,
  daysHoursSlider: false,
};

const mapStateToProps = (state: any) => ({
  contract: state.Contract.contract,
  mapInitialized: state.Map.initialized,
  mapConfig: state.Map?.config,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setConfigParam: (key: any, value: any) =>
    dispatch(MapActioner.setConfigParam(key, value)),
  retrieveAllParkStats: (allAreas: any) =>
    dispatch(ParkStatisticsActioner.retrieveAllStats(allAreas)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Map);
